"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useReducer,
  useState,
} from "react";

import { ApiError } from "@/lib/api";
import { getWorkspaceSession } from "@/lib/api/administration";
import {
  getWorkspaceContext,
  type WorkspaceContext,
} from "@/lib/api/suppliers";
import { mapRequestOutcome } from "@/lib/commands/outcome";
import type { RequestCompletion } from "@/lib/commands/request-completion";
import {
  workspaceOperationsReducer,
  type WorkspaceOperationNotice,
} from "@/lib/commands/workspace-operations";
import {
  clearSourcingSessionOperations,
  persistSourcingSessionOperation,
  removeSourcingSessionOperation,
  restoreSourcingSessionOperations,
} from "@/lib/commands/sourcing-session-operations";

export type WorkspaceStatus =
  | "loading"
  | "ready"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "unavailable";
interface Value {
  requestCompletion?: RequestCompletion;
  completeRequest: (completion: RequestCompletion) => void;
  requestCreateGeneration: number;
  sourcingCreateGeneration: number;
  supplierCompletionGeneration: number;
  context?: WorkspaceContext;
  status: WorkspaceStatus;
  reload: () => void;
  refreshAuthority: (error?: unknown) => void;
  getAuthorityEpoch: () => number;
  isAuthorityCurrent: (epoch: number) => boolean;
  operations: WorkspaceOperationNotice[];
  startOperation: (operationType: string, idempotencyKey: string) => void;
  markOperationUnknown: (idempotencyKey: string) => void;
  resolveOperation: (idempotencyKey: string, resultState: string) => void;
  clearOperation: (idempotencyKey: string) => void;
  commandPending: boolean;
  acquireCommand: () => boolean;
  releaseCommand: (epoch: number) => void;
}
type ContextState = Pick<Value, "context" | "status">;
const Context = createContext<Value | null>(null);

async function load(signal: AbortSignal) {
  const session = await getWorkspaceSession({ signal });
  return (
    await getWorkspaceContext({ tenantId: session.data.tenantId, signal })
  ).data;
}

function errorStatus(error: unknown): WorkspaceStatus {
  const outcome = mapRequestOutcome(error);
  if (outcome === "unauthorized") return "unauthorized";
  if (outcome === "forbidden") return "forbidden";
  if (outcome === "notFound") return "notFound";
  return "unavailable";
}

function operationStore() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function WorkspaceContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<ContextState>({ status: "loading" });
  const [generation, setGeneration] = useState(0);
  const [requestCompletion, completeRequest] = useState<RequestCompletion>();
  const [requestCreateGeneration, retireRequestCreate] = useState(0);
  const [sourcingCreateGeneration, retireSourcingCreate] = useState(0);
  const [supplierCompletionGeneration, retireSupplierIntent] = useState(0);
  const [operations, dispatchOperation] = useReducer(
    workspaceOperationsReducer,
    [],
  );
  const commandFlight = useRef(false);
  const authorityEpoch = useRef(0);
  const contextLoad = useRef<AbortController | null>(null);
  const [commandPending, setCommandPending] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    contextLoad.current = controller;
    const epoch = authorityEpoch.current;
    void load(controller.signal).then(
      (context) => {
        if (controller.signal.aborted || authorityEpoch.current !== epoch)
          return;
        const store = operationStore();
        if (store)
          dispatchOperation({
            type: "restore-sourcing",
            notices: restoreSourcingSessionOperations(store, {
              tenantId: context.tenantId,
              accountId: context.accountId,
            }),
          });
        setState({ status: "ready", context });
      },
      (error) => {
        if (
          !controller.signal.aborted &&
          authorityEpoch.current === epoch &&
          !(error instanceof ApiError && error.kind === "cancelled")
        ) {
          const status = errorStatus(error);
          if (
            status === "unauthorized" ||
            status === "forbidden" ||
            status === "notFound"
          ) {
            const store = operationStore();
            if (store) clearSourcingSessionOperations(store);
            dispatchOperation({ type: "reset" });
            completeRequest(undefined);
          }
          setState({ status });
        }
      },
    );
    return () => controller.abort();
  }, [generation]);
  return (
    <Context.Provider
      value={{
        ...state,
        requestCompletion,
        requestCreateGeneration,
        sourcingCreateGeneration,
        supplierCompletionGeneration,
        completeRequest: (completion) => {
          completeRequest(completion);
          if (completion.operationType === "request.create")
            retireRequestCreate((value) => value + 1);
          if (completion.operationType === "sourcing.create")
            retireSourcingCreate((value) => value + 1);
          if (completion.operationType.startsWith("supplier."))
            retireSupplierIntent((value) => value + 1);
        },
        operations,
        commandPending,
        acquireCommand: () => {
          if (commandFlight.current) return false;
          commandFlight.current = true;
          setCommandPending(true);
          return true;
        },
        releaseCommand: (epoch) => {
          if (authorityEpoch.current !== epoch) return;
          commandFlight.current = false;
          setCommandPending(false);
        },
        reload: () => {
          authorityEpoch.current += 1;
          contextLoad.current?.abort();
          commandFlight.current = false;
          setCommandPending(false);
          setState({ status: "loading" });
          setGeneration((value) => value + 1);
        },
        refreshAuthority: (error) => {
          authorityEpoch.current += 1;
          contextLoad.current?.abort();
          commandFlight.current = false;
          setCommandPending(false);
          const store = operationStore();
          if (store) clearSourcingSessionOperations(store);
          dispatchOperation({ type: "reset" });
          completeRequest(undefined);
          setState({ status: error ? errorStatus(error) : "notFound" });
        },
        getAuthorityEpoch: () => authorityEpoch.current,
        isAuthorityCurrent: (epoch) => authorityEpoch.current === epoch,
        startOperation: (operationType, idempotencyKey) => {
          const store = operationStore();
          if (store && state.context && operationType.startsWith("sourcing."))
            persistSourcingSessionOperation(
              store,
              {
                tenantId: state.context.tenantId,
                accountId: state.context.accountId,
              },
              operationType,
              idempotencyKey,
            );
          dispatchOperation({
            type: "start",
            notice: { operationType, idempotencyKey, status: "pending" },
          });
        },
        markOperationUnknown: (idempotencyKey) =>
          dispatchOperation({ type: "unknown", idempotencyKey }),
        resolveOperation: (idempotencyKey, resultState) => {
          if (["completed", "failed"].includes(resultState.toLowerCase())) {
            const store = operationStore();
            if (store) removeSourcingSessionOperation(store, idempotencyKey);
          }
          dispatchOperation({ type: "resolved", idempotencyKey, resultState });
        },
        clearOperation: (idempotencyKey) => {
          const store = operationStore();
          if (store) removeSourcingSessionOperation(store, idempotencyKey);
          dispatchOperation({ type: "clear", idempotencyKey });
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useWorkspaceContext() {
  const value = useContext(Context);
  if (!value) throw new Error("Workspace context is unavailable");
  return value;
}

export function useReadyWorkspaceContext() {
  const value = useWorkspaceContext();
  if (!value.context) throw new Error("Workspace context is not ready");
  return value.context;
}
